import { InsuranceOverviewPanel } from './InsuranceOverviewPanel';
import { Box, Skeleton, Stack } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { InsuranceExpressLanding } from './InsuranceExpressLanding';
import { useFetchInsuranceData } from '../hooks/useFetchInsuranceData';
import { useDispatch, useSelector } from 'react-redux';
import { ReactNode, useEffect, useState } from 'react';
import { insuranceActions, insuranceSelector } from '../model';
import { FormProvider, useForm } from 'react-hook-form';
import { FinanceProgramFormValues } from 'modules/desking/types';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { CreditApplication, ProvidingInsuranceType, VendorOpportunity } from '@trnsact/trnsact-shared-types';
import { useModal } from 'hooks/useModal';
import { ModalsKeys } from 'global';
import { SendInsuranceQuoteDialog } from './dialogs/sendInsuranceQuoteDialog/SendInsuranceQuoteDialog';
import { ModalsContainer } from 'components/shared/Modals';
import { InsuranceSelectSubmissionDialog, InsuranceEnterManualLenderRequirementsDialog } from './dialogs';

interface Props {
  creditApp: CreditApplication;
  vo: VendorOpportunity;
}

type InsuranceTabFormValues = FinanceProgramFormValues & {
  coverageRequirements: string;
  certificateRequirements: string;
  otherInstructions: string;
};

export const InsuranceTab = ({ vo, creditApp }: Props) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const methods = useForm<InsuranceTabFormValues>({
    defaultValues: {
      coverageRequirements: '',
      certificateRequirements: '',
      otherInstructions: '',
    },
    shouldUnregister: false,
  });

  const { isFetching } = useFetchInsuranceData(vo as any, creditApp);
  const [isRedirectingToFinanceProgramsPage, setIsRedirectingToFinanceProgramsPage] = useState(false);

  const isInsuranceExpressIntegrated = useSelector(insuranceSelector.isInsuranceExpressIntegrated);

  const insuranceQuote = useSelector(insuranceSelector.insuranceQuote);
  const proposals = useSelector(insuranceSelector.proposals);

  const certificateOfInsurance = useSelector(insuranceSelector.insuranceCertificate);

  const { handleOpen: openSendInsuranceQuoteModal } = useModal(ModalsKeys.SendInsuranceQuoteDialog);
  const { handleOpen: openSelectSubmissionDialog } = useModal(ModalsKeys.InsuranceSelectSubmissionDialog);
  const { handleOpen: openEnterManualLenderRequirementsDialog } = useModal(
    ModalsKeys.InsuranceEnterManualLenderRequirementsDialog
  );

  const openSendInsuranceQuoteModalWrapper = () => {
    console.log('open send insurance quote modal');
    openSendInsuranceQuoteModal();
  };

  const openSelectSubmissionDialogWrapper = () => {
    console.log('open select submission modal');
    openSelectSubmissionDialog();
  };

  const openEnterManualLenderRequirementsDialogWrapper = () => {
    console.log('open enter manual lender requirements modal');
    openEnterManualLenderRequirementsDialog();
  };

  const getCertificateOfInsuranceStatus = () => {
    if (certificateOfInsurance && certificateOfInsurance.creditSubmissionId) {
      return 'Sent To Lender';
    }
    if (certificateOfInsurance) {
      return 'Received';
    }
    return 'Not Received';
  };

  useEffect(() => {
    const coverage = insuranceQuote?.insuranceCoverage;

    methods.reset(
      {
        ...methods.getValues(),
        coverageRequirements: coverage?.coverageRequirements ?? '',
        certificateRequirements: coverage?.certificateRequirements ?? '',
        otherInstructions: coverage?.otherInstructions ?? '',
      },
      { keepDirty: false, keepTouched: false }
    );
  }, [insuranceQuote?.insuranceCoverage, methods]);

  const modalsWithKeys: Partial<Record<ModalsKeys, ReactNode>> = {
    [ModalsKeys.SendInsuranceQuoteDialog]: <SendInsuranceQuoteDialog />,
    [ModalsKeys.InsuranceSelectSubmissionDialog]: <InsuranceSelectSubmissionDialog />,
    [ModalsKeys.InsuranceEnterManualLenderRequirementsDialog]: <InsuranceEnterManualLenderRequirementsDialog />,
  };

  useEffect(
    () => () => {
      dispatch(insuranceActions.clearState());
    },
    [dispatch]
  );

  return (
    <DndProvider backend={HTML5Backend}>
      <FormProvider {...methods}>
        <Box className={classes.root}>
          {!isFetching && !isRedirectingToFinanceProgramsPage ? (
            !isInsuranceExpressIntegrated ? (
              <InsuranceExpressLanding />
            ) : (
              <InsuranceOverviewPanel
                applicantIntent={
                  creditApp.providingInsurance === ProvidingInsuranceType.ReceiveQuote
                    ? 'Requested Quote'
                    : 'Providing Own Coverage'
                }
                coiStatus={getCertificateOfInsuranceStatus()}
                creditApp={creditApp}
                vo={vo}
                onSendInsuranceQuoteToApplicant={openSendInsuranceQuoteModalWrapper}
                onAddAgentDetails={() => console.log('add agent')}
                onUploadCoiManually={() => console.log('upload coi')}
                onEnterRequirements={openEnterManualLenderRequirementsDialogWrapper}
                onSelectSubmission={openSelectSubmissionDialogWrapper}
                onSetupFinanceProgram={() => {
                  setIsRedirectingToFinanceProgramsPage(true);
                  window.history.pushState({}, '', '/finance-programs');
                  window.location.reload();
                }}
              />
            )
          ) : (
            <Skeleton variant="rectangular" height="300px" width="100%" />
          )}
          <ModalsContainer modalsWithKeys={modalsWithKeys} />
        </Box>
      </FormProvider>
    </DndProvider>
  );
};

const useStyles = makeStyles({
  root: {
    backgroundColor: 'white',
    color: '#000000de',
  },
});
